import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Typography,
  Alert,
  Button,
  Switch,
  List,
  ListItem,
  Divider,
  Box,
  TextField,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Rating,
} from '@mui/material';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import { AppProvider } from '@toolpad/core/AppProvider';
import { businessService } from '../../services/api/businessService';
import { accountService } from '../../services/api/accountService';
import { reviewsService } from '../../services/api/reviewsService';
import { useAuth } from '../../context/AuthContext';
import { appTheme } from '../../theme/appTheme';
import NkySnackbarAlert from '../common/NkySnackbarAlert/NkySnackbarAlert';
import NkySpinner from '../common/NkySpinner/NkySpinner';
import AdminMetricsPanel from '../AdminMetricsPanel/AdminMetricsPanel';
import AdminCrm from '../AdminCrm/AdminCrm';
import AdminWaitlist from '../AdminWaitlist/AdminWaitlist';
import AdminUsers from '../AdminUsers/AdminUsers';
import AdminNavigationBar, {
  getAdminTabTitle,
} from '../AdminNavigationBar/AdminNavigationBar';
import AdminBusinessEditModal from '../AdminBusinessEditModal/AdminBusinessEditModal';
import {
  buildAdminBusinessEditPatch,
  createAdminBusinessEditFormFromBusiness,
  isAdminBusinessEditUnchanged,
} from '../AdminBusinessEditModal/adminBusinessEditForm';
import {
  DashboardContainer,
  ContentWrapper,
} from '../BusinessDashboard/BusinessDashboard.styled';
import {
  AdminPageOuter,
  AdminSidebarContainer,
  AdminHeaderBand,
  AdminPageTitle,
  AdminSectionTitle,
  AdminListSection,
  AdminBusinessNameButton,
  AdminGooglePlaceIcon,
} from './AdminPage.styled';

const GOOGLE_LOGO_URL =
  'https://www.gstatic.com/images/branding/googlelogo/svg/googlelogo_clr_74x24px.svg';

function uniqueBusinessEmails(businesses) {
  const seen = new Set();
  const emails = [];
  for (const biz of businesses || []) {
    const email = String(biz?.BusinessEmail || '').trim();
    if (!email) continue;
    const key = email.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    emails.push(email);
  }
  return emails;
}

function nkyCpsScoreValue(nkyCps) {
  const score = nkyCps?.Score;
  return typeof score === 'number' && !Number.isNaN(score) ? score : null;
}

function nkyCpsTooltipContent(nkyCps) {
  const score = nkyCpsScoreValue(nkyCps);
  if (score == null) {
    return (
      <>
        NKYCPS not computed yet.
        <br />
        Use Metrics → Recompute NKYCPS or wait for the weekly job.
      </>
    );
  }
  const breakdown = nkyCps?.Breakdown;
  if (!breakdown) {
    return <>NKYCPS: {score}</>;
  }
  return (
    <>
      NKYCPS: {score}
      <br />
      Reputation: {breakdown.Reputation}
      <br />
      Completeness: {breakdown.Completeness}
      <br />
      Engagement: {breakdown.Engagement}
      <br />
      Tenure bonus: +{breakdown.TenureBonus}
    </>
  );
}

function firstPublishedTooltip(iso, published) {
  if (!iso || typeof iso !== 'string') {
    return published
      ? 'First published date is not on file yet.'
      : 'This business has not been published yet.';
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return published
      ? 'First published date is not on file yet.'
      : 'This business has not been published yet.';
  }
  const timestamp = d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  return `First published ${timestamp}`;
}

function AdminPage() {
  const { user, refreshUser } = useAuth();
  const [tabValue, setTabValue] = useState('metrics');
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [toast, setToast] = useState({
    open: false,
    severity: 'success',
    message: '',
  });
  const [pendingIds, setPendingIds] = useState(() => new Set());
  const [reviewGroups, setReviewGroups] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsError, setReviewsError] = useState(null);
  const [pendingReviewIds, setPendingReviewIds] = useState(() => new Set());
  const [editBusinessId, setEditBusinessId] = useState(null);
  const [editForm, setEditForm] = useState(() =>
    createAdminBusinessEditFormFromBusiness(null),
  );
  const [editSaving, setEditSaving] = useState(false);
  const [businessSearch, setBusinessSearch] = useState('');
  const [impersonationTargetId, setImpersonationTargetId] = useState(null);

  const sorted = useMemo(
    () =>
      [...businesses].sort((a, b) =>
        (a.BusinessName || '').localeCompare(b.BusinessName || '', undefined, {
          sensitivity: 'base',
        }),
      ),
    [businesses],
  );

  const anyPartnershipPending = useMemo(
    () => sorted.some((b) => !b.PartnershipAgreementAcceptedAt),
    [sorted],
  );
  const currentBusinessId = user?.BusinessId || null;
  const currentBusinessName = useMemo(
    () =>
      sorted.find((biz) => biz.BusinessId === currentBusinessId)
        ?.BusinessName || null,
    [sorted, currentBusinessId],
  );
  const filteredBusinesses = useMemo(() => {
    const query = businessSearch.trim().toLowerCase();
    if (!query) {
      return sorted;
    }
    return sorted.filter((biz) =>
      (biz.BusinessName || '').toLowerCase().includes(query),
    );
  }, [sorted, businessSearch]);

  const businessEmails = useMemo(
    () => uniqueBusinessEmails(sorted),
    [sorted],
  );

  const editBusiness = useMemo(
    () => sorted.find((biz) => biz.BusinessId === editBusinessId) ?? null,
    [sorted, editBusinessId],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await businessService.getAllBusinessesForAdmin();
      setBusinesses(Array.isArray(data) ? data : []);
    } catch (e) {
      setLoadError(e.message || 'Failed to load businesses');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const loadPendingAdminReviews = useCallback(async () => {
    setReviewsLoading(true);
    setReviewsError(null);
    try {
      const flat = await reviewsService.getReviewsForAdmin({
        IsReviewedByAdmin: false,
      });
      if (!Array.isArray(flat) || flat.length === 0) {
        setReviewGroups([]);
        return;
      }

      const byBiz = new Map();
      for (const r of flat) {
        const bid = r.BusinessId;
        if (!bid) continue;
        if (!byBiz.has(bid)) {
          byBiz.set(bid, {
            businessId: bid,
            businessName: r.BusinessName || '—',
            reviews: [],
          });
        }
        byBiz.get(bid).reviews.push(r);
      }

      setReviewGroups(
        [...byBiz.values()].sort((a, b) =>
          a.businessName.localeCompare(b.businessName, undefined, {
            sensitivity: 'base',
          }),
        ),
      );
    } catch (e) {
      setReviewsError(e.message || 'Failed to load pending reviews');
    } finally {
      setReviewsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tabValue === 'reviews') {
      loadPendingAdminReviews();
    }
  }, [tabValue, loadPendingAdminReviews]);

  const handlePublishedChange = async (biz, nextPublished) => {
    const id = biz.BusinessId;
    setPendingIds((prev) => new Set(prev).add(id));
    try {
      const updated = await businessService.editBusiness(id, {
        BusinessPublished: nextPublished,
      });
      setBusinesses((prev) =>
        prev.map((b) =>
          b.BusinessId === id ? { ...b, ...updated } : b,
        ),
      );
      setToast({
        open: true,
        severity: 'success',
        message: nextPublished
          ? 'Business is now published.'
          : 'Business is now unpublished.',
      });
    } catch (e) {
      setToast({
        open: true,
        severity: 'error',
        message: e.message || 'Update failed',
      });
    } finally {
      setPendingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const handleAnalyticsAccessChange = async (biz, nextEnabled) => {
    const id = biz.BusinessId;
    setPendingIds((prev) => new Set(prev).add(id));
    try {
      await businessService.editBusiness(id, {
        HasAnalyticsAccess: nextEnabled,
      });
      setBusinesses((prev) =>
        prev.map((b) =>
          b.BusinessId === id ? { ...b, HasAnalyticsAccess: nextEnabled } : b,
        ),
      );
      setToast({
        open: true,
        severity: 'success',
        message: nextEnabled
          ? 'Analytics enabled for this business.'
          : 'Analytics disabled for this business.',
      });
    } catch (e) {
      setToast({
        open: true,
        severity: 'error',
        message: e.message || 'Update failed',
      });
    } finally {
      setPendingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const openBusinessEdit = (biz) => {
    setEditBusinessId(biz.BusinessId);
    setEditForm(createAdminBusinessEditFormFromBusiness(biz));
  };

  const closeBusinessEdit = () => {
    if (editSaving) {
      return;
    }
    setEditBusinessId(null);
    setEditForm(createAdminBusinessEditFormFromBusiness(null));
  };

  const handleEditFieldChange = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleBusinessEditSave = async () => {
    if (!editBusiness) {
      return;
    }

    const id = editBusiness.BusinessId;
    const patch = buildAdminBusinessEditPatch(editForm, editBusiness);
    if (Object.keys(patch).length === 0) {
      closeBusinessEdit();
      return;
    }

    setEditSaving(true);
    try {
      const updated = await businessService.editBusiness(id, patch);
      const draftPlaceId = String(editForm.googlePlaceId ?? '').trim();
      setBusinesses((prev) =>
        prev.map((b) =>
          b.BusinessId === id
            ? {
                ...b,
                ...updated,
                GoogleReview:
                  updated.GoogleReview ??
                  (patch.GoogleReview
                    ? { PlaceId: draftPlaceId || null }
                    : b.GoogleReview),
              }
            : b,
        ),
      );
      setToast({
        open: true,
        severity: 'success',
        message: patch.GoogleReview
          ? draftPlaceId
            ? 'Google Place ID saved.'
            : 'Google Place ID cleared.'
          : 'Business settings saved.',
      });
      setEditBusinessId(null);
      setEditForm(createAdminBusinessEditFormFromBusiness(null));
    } catch (e) {
      setToast({
        open: true,
        severity: 'error',
        message: e.message || 'Failed to save business settings',
      });
    } finally {
      setEditSaving(false);
    }
  };

  const handleMarkReviewed = async (businessId, reviewId) => {
    const key = `${businessId}:${reviewId}`;
    setPendingReviewIds((prev) => new Set(prev).add(key));
    try {
      await reviewsService.updateReviewDecision({
        businessId,
        reviewId,
        action: 'MarkReviewed',
      });

      setReviewGroups((prev) =>
        prev
          .map((group) =>
            group.businessId === businessId
              ? {
                  ...group,
                  reviews: group.reviews.filter((r) => r.ReviewId !== reviewId),
                }
              : group,
          )
          .filter((group) => group.reviews.length > 0),
      );

      setToast({
        open: true,
        severity: 'success',
        message: 'Marked as reviewed.',
      });
    } catch (e) {
      setToast({
        open: true,
        severity: 'error',
        message: e.message || 'Failed to update review',
      });
    } finally {
      setPendingReviewIds((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const handleReviewPublishedToggle = async (
    businessId,
    reviewId,
    nextPublished,
  ) => {
    const key = `${businessId}:${reviewId}`;
    setPendingReviewIds((prev) => new Set(prev).add(key));
    try {
      await reviewsService.updateReviewDecision({
        businessId,
        reviewId,
        action: nextPublished ? 'Publish' : 'Unpublish',
      });

      setReviewGroups((prev) =>
        prev.map((group) =>
          group.businessId === businessId
            ? {
                ...group,
                reviews: group.reviews.map((r) =>
                  r.ReviewId === reviewId
                    ? { ...r, IsPublished: nextPublished }
                    : r,
                ),
              }
            : group,
        ),
      );

      setToast({
        open: true,
        severity: 'success',
        message: nextPublished
          ? 'Review is now published.'
          : 'Review is now unpublished.',
      });
    } catch (e) {
      setToast({
        open: true,
        severity: 'error',
        message: e.message || 'Failed to update publish state',
      });
    } finally {
      setPendingReviewIds((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const handleImpersonateBusiness = async (businessId) => {
    if (!businessId) return;
    setImpersonationTargetId(businessId);
    try {
      await accountService.updateAccount({ BusinessId: businessId });
      await refreshUser();
      setToast({
        open: true,
        severity: 'success',
        message: 'Impersonation updated. You are now acting as this business.',
      });
    } catch (e) {
      setToast({
        open: true,
        severity: 'error',
        message: e.message || 'Failed to impersonate business',
      });
    } finally {
      setImpersonationTargetId(null);
    }
  };

  const handleCopyBusinessEmails = async () => {
    if (!businessEmails.length) {
      setToast({
        open: true,
        severity: 'warning',
        message: 'No business emails to copy.',
      });
      return;
    }
    try {
      await navigator.clipboard.writeText(businessEmails.join(', '));
      setToast({
        open: true,
        severity: 'success',
        message: `Copied ${businessEmails.length} business email${
          businessEmails.length === 1 ? '' : 's'
        } to the clipboard.`,
      });
    } catch (e) {
      setToast({
        open: true,
        severity: 'error',
        message: e.message || 'Could not copy emails to the clipboard.',
      });
    }
  };

  return (
    <AppProvider branding={{ title: 'Admin' }} theme={appTheme}>
      <AdminPageOuter>
        <DashboardContainer>
          <AdminSidebarContainer>
            <AdminNavigationBar
              activeTab={tabValue}
              onTabChange={setTabValue}
            />
          </AdminSidebarContainer>

          <ContentWrapper>
            <AdminHeaderBand>
              <AdminPageTitle variant="h4">Admin</AdminPageTitle>
              <Typography variant="body2" sx={{ color: '#5a6478' }}>
                Manage storefront visibility, assign users to businesses, triage
                pending reviews, track outreach prospects, review unmatched
                homeowner demand, and view platform metrics.
              </Typography>
            </AdminHeaderBand>

            <AdminListSection>
              <AdminSectionTitle variant="h5">
                {getAdminTabTitle(tabValue)}
              </AdminSectionTitle>
              {tabValue === 'metrics' && (
                <AdminMetricsPanel onNavigateTab={setTabValue} />
              )}
              {tabValue === 'crm' && <AdminCrm businesses={businesses} />}
              {tabValue === 'waitlist' && (
                <AdminWaitlist
                  businesses={businesses}
                  businessesLoading={loading}
                  businessesError={loadError}
                />
              )}
              {tabValue === 'users' && (
                <AdminUsers
                  businesses={businesses}
                  businessesLoading={loading}
                  businessesError={loadError}
                />
              )}
              {tabValue !== 'metrics' &&
                tabValue !== 'crm' &&
                tabValue !== 'users' &&
                tabValue !== 'waitlist' &&
                loading && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    py: 6,
                    px: 2,
                  }}
                >
                  <NkySpinner />
                </Box>
              )}
              {tabValue !== 'metrics' &&
                tabValue !== 'crm' &&
                tabValue !== 'users' &&
                tabValue !== 'waitlist' &&
                !loading &&
                loadError && (
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                  {loadError}
                </Alert>
              )}
              {tabValue !== 'metrics' &&
                tabValue !== 'crm' &&
                tabValue !== 'users' &&
                tabValue !== 'waitlist' &&
                !loading &&
                !loadError &&
                sorted.length === 0 && (
                  <Typography sx={{ py: 3, color: '#5a6478' }}>
                    No businesses found.
                  </Typography>
                )}
              {tabValue !== 'metrics' &&
                tabValue !== 'crm' &&
                tabValue !== 'users' &&
                tabValue !== 'waitlist' &&
                !loading &&
                !loadError &&
                sorted.length > 0 && (
                  <>
                    {tabValue === 'businesses' && (
                      <>
                        <Box
                          sx={{
                            mb: 2,
                            p: 1.5,
                            borderRadius: '12px',
                            bgcolor: 'rgba(13, 78, 254, 0.05)',
                            border: '1px solid rgba(13, 78, 254, 0.12)',
                          }}
                        >
                          <Typography variant="body2" sx={{ color: '#5a6478' }}>
                            Current impersonation:{' '}
                            <Box
                              component="span"
                              sx={{ color: '#1a1a2e', fontWeight: 700 }}
                            >
                              {currentBusinessName || 'None selected'}
                            </Box>
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1.5,
                            mb: 2,
                            flexWrap: 'wrap',
                          }}
                        >
                          <TextField
                            fullWidth
                            size="small"
                            label="Search business name"
                            value={businessSearch}
                            onChange={(event) =>
                              setBusinessSearch(event.target.value)
                            }
                            sx={{ flex: '1 1 240px', minWidth: 0 }}
                          />
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<ContentCopyOutlinedIcon />}
                            onClick={handleCopyBusinessEmails}
                            disabled={!businessEmails.length}
                            sx={{
                              textTransform: 'none',
                              fontWeight: 600,
                              borderRadius: '10px',
                              whiteSpace: 'nowrap',
                              color: '#0d4efe',
                              borderColor: 'rgba(13, 78, 254, 0.4)',
                              '&:hover': {
                                borderColor: '#0d4efe',
                                backgroundColor: 'rgba(13, 78, 254, 0.06)',
                              },
                            }}
                          >
                            Copy all emails
                          </Button>
                        </Box>
                        {anyPartnershipPending && (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 1.5,
                              mb: 2,
                              p: 1.5,
                              borderRadius: '12px',
                              bgcolor: 'rgba(237, 108, 2, 0.08)',
                              border: '1px solid rgba(237, 108, 2, 0.28)',
                            }}
                          >
                            <HandshakeOutlinedIcon
                              sx={{
                                color: '#ed6c02',
                                fontSize: 22,
                                flexShrink: 0,
                                mt: 0.25,
                              }}
                              aria-hidden
                            />
                            <Typography
                              variant="body2"
                              sx={{ color: '#5a6478', lineHeight: 1.55 }}
                            >
                              <Box
                                component="span"
                                sx={{ fontWeight: 700, color: '#1a1a2e' }}
                              >
                                Partnership pending
                              </Box>{' '}
                              — handshake icon next to a business means the
                              Business Partnership Agreement is not on file yet.
                              Publishing stays off until they accept it in the
                              provider dashboard.
                            </Typography>
                          </Box>
                        )}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 2,
                            flexWrap: 'wrap',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ color: '#5a6478', fontWeight: 600 }}
                          >
                            NKYCPS
                          </Typography>
                          <Chip
                            size="small"
                            label="82.4"
                            aria-hidden
                            sx={{
                              height: 22,
                              fontSize: '0.7rem',
                              fontWeight: 700,
                              bgcolor: 'rgba(13, 78, 254, 0.08)',
                              color: '#0d4efe',
                              border: '1px solid rgba(13, 78, 254, 0.2)',
                            }}
                          />
                          <Typography variant="body2" sx={{ color: '#5a6478' }}>
                            — NKY Connect Partner Score (0–100). Hover the
                            published toggle for first published date.
                          </Typography>
                          <Chip
                            size="small"
                            icon={
                              <CreditCardOutlinedIcon
                                sx={{ fontSize: '14px !important' }}
                              />
                            }
                            label="Billing set up"
                            aria-hidden
                            sx={{
                              height: 22,
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              bgcolor: 'rgba(1, 163, 122, 0.1)',
                              color: '#01a37a',
                              border: '1px solid rgba(1, 163, 122, 0.28)',
                              '& .MuiChip-icon': {
                                color: 'inherit',
                                ml: '6px',
                              },
                            }}
                          />
                          <Typography variant="body2" sx={{ color: '#5a6478' }}>
                            means a payment method and subscription agreement
                            are on file.
                          </Typography>
                        </Box>
                        {filteredBusinesses.length === 0 && (
                          <Typography sx={{ py: 3, color: '#5a6478' }}>
                            No businesses match your search.
                          </Typography>
                        )}
                        {filteredBusinesses.length > 0 && (
                        <List disablePadding>
                          {filteredBusinesses.map((biz, index) => {
                            const published = Boolean(biz.BusinessPublished);
                            const analyticsEnabled = Boolean(
                              biz.HasAnalyticsAccess,
                            );
                            const canPublish = Boolean(
                              biz.PartnershipAgreementAcceptedAt,
                            );
                            const busy = pendingIds.has(biz.BusinessId);
                            const isCurrent =
                              biz.BusinessId === currentBusinessId;
                            const impersonateBusy =
                              impersonationTargetId === biz.BusinessId;
                            const partnershipPending =
                              !biz.PartnershipAgreementAcceptedAt;
                            const hasGooglePlaceId = Boolean(
                              String(biz.GoogleReview?.PlaceId ?? '').trim(),
                            );
                            const hasBillingSetup = Boolean(
                              biz.SubscriptionAgreedAt,
                            );
                            const publishedAtTitle = firstPublishedTooltip(
                              biz.FirstPublishedAt,
                              published,
                            );
                            return (
                              <React.Fragment key={biz.BusinessId}>
                                {index > 0 && (
                                  <Divider
                                    component="li"
                                    sx={{ opacity: 0.6 }}
                                  />
                                )}
                                <ListItem
                                  sx={{
                                    py: 1.5,
                                    flexWrap: 'wrap',
                                    gap: 1,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1,
                                      flex: '1 1 200px',
                                      minWidth: 0,
                                      flexWrap: 'wrap',
                                    }}
                                  >
                                    {partnershipPending && (
                                      <Tooltip title="Partnership agreement not accepted — provider must complete this in their dashboard before you can publish.">
                                        <HandshakeOutlinedIcon
                                          sx={{
                                            color: '#ed6c02',
                                            fontSize: 22,
                                            flexShrink: 0,
                                          }}
                                        />
                                      </Tooltip>
                                    )}
                                    <Tooltip title="Edit business settings">
                                      <AdminBusinessNameButton
                                        onClick={() => openBusinessEdit(biz)}
                                        disabled={busy}
                                      >
                                        {biz.BusinessName || '—'}
                                      </AdminBusinessNameButton>
                                    </Tooltip>
                                    <Tooltip
                                      title={nkyCpsTooltipContent(biz.NKYCPS)}
                                    >
                                      <Chip
                                        size="small"
                                        label={
                                          nkyCpsScoreValue(biz.NKYCPS) != null
                                            ? String(
                                                nkyCpsScoreValue(biz.NKYCPS),
                                              )
                                            : '—'
                                        }
                                        sx={{
                                          height: 22,
                                          fontSize: '0.7rem',
                                          fontWeight: 700,
                                          flexShrink: 0,
                                          bgcolor:
                                            nkyCpsScoreValue(biz.NKYCPS) != null
                                              ? 'rgba(13, 78, 254, 0.08)'
                                              : 'rgba(90, 100, 120, 0.08)',
                                          color:
                                            nkyCpsScoreValue(biz.NKYCPS) != null
                                              ? '#0d4efe'
                                              : '#8893a4',
                                          border: '1px solid',
                                          borderColor:
                                            nkyCpsScoreValue(biz.NKYCPS) != null
                                              ? 'rgba(13, 78, 254, 0.2)'
                                              : 'rgba(90, 100, 120, 0.2)',
                                        }}
                                      />
                                    </Tooltip>
                                    {hasGooglePlaceId && (
                                      <Tooltip title="Google Place ID linked">
                                        <Box
                                          component="span"
                                          sx={{
                                            display: 'inline-flex',
                                            flexShrink: 0,
                                          }}
                                        >
                                          <AdminGooglePlaceIcon
                                            src={GOOGLE_LOGO_URL}
                                            alt="Google"
                                          />
                                        </Box>
                                      </Tooltip>
                                    )}
                                    <Tooltip
                                      title={
                                        hasBillingSetup
                                          ? 'This provider has completed billing setup (subscription agreement on file).'
                                          : 'This provider has not completed billing setup.'
                                      }
                                    >
                                      <Chip
                                        size="small"
                                        icon={
                                          <CreditCardOutlinedIcon
                                            sx={{ fontSize: '14px !important' }}
                                          />
                                        }
                                        label={
                                          hasBillingSetup
                                            ? 'Billing set up'
                                            : 'No billing'
                                        }
                                        sx={{
                                          height: 22,
                                          fontSize: '0.7rem',
                                          fontWeight: 600,
                                          bgcolor: hasBillingSetup
                                            ? 'rgba(1, 163, 122, 0.1)'
                                            : 'rgba(90, 100, 120, 0.08)',
                                          color: hasBillingSetup
                                            ? '#01a37a'
                                            : '#8893a4',
                                          border: '1px solid',
                                          borderColor: hasBillingSetup
                                            ? 'rgba(1, 163, 122, 0.28)'
                                            : 'rgba(90, 100, 120, 0.2)',
                                          '& .MuiChip-icon': {
                                            color: 'inherit',
                                            ml: '6px',
                                          },
                                        }}
                                      />
                                    </Tooltip>
                                  </Box>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1,
                                      flexWrap: 'wrap',
                                    }}
                                  >
                                    <Tooltip title={publishedAtTitle}>
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 0.75,
                                          minWidth: 148,
                                        }}
                                      >
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color: busy
                                            ? '#5a6478'
                                            : published
                                              ? '#01a37a'
                                              : '#5a6478',
                                          minWidth: 88,
                                        }}
                                      >
                                        {busy
                                          ? 'Saving…'
                                          : published
                                            ? 'Published'
                                            : 'Unpublished'}
                                      </Typography>
                                      <Switch
                                        checked={published}
                                        disabled={
                                          busy || (!published && !canPublish)
                                        }
                                        onChange={(_, checked) =>
                                          handlePublishedChange(biz, checked)
                                        }
                                        inputProps={{
                                          'aria-label': `${published ? 'Unpublish' : 'Publish'} ${biz.BusinessName || 'business'}`,
                                        }}
                                        sx={{
                                          '& .MuiSwitch-switchBase.Mui-checked':
                                            {
                                              color: '#01ebb2',
                                            },
                                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':
                                            {
                                              background:
                                                'linear-gradient(90deg, #01ebb2 0%, #0d4efe 100%)',
                                              opacity: 1,
                                            },
                                        }}
                                      />
                                      </Box>
                                    </Tooltip>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.75,
                                        minWidth: 148,
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color: busy
                                            ? '#5a6478'
                                            : analyticsEnabled
                                              ? '#01a37a'
                                              : '#5a6478',
                                          minWidth: 72,
                                        }}
                                      >
                                        Analytics
                                      </Typography>
                                      <Tooltip title="When enabled, this business can view storefront analytics in their provider dashboard.">
                                        <Switch
                                          checked={analyticsEnabled}
                                          disabled={busy}
                                          onChange={(_, checked) =>
                                            handleAnalyticsAccessChange(
                                              biz,
                                              checked,
                                            )
                                          }
                                          inputProps={{
                                            'aria-label': `${analyticsEnabled ? 'Disable' : 'Enable'} analytics for ${biz.BusinessName || 'business'}`,
                                          }}
                                          sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked':
                                              {
                                                color: '#01ebb2',
                                              },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':
                                              {
                                                background:
                                                  'linear-gradient(90deg, #01ebb2 0%, #0d4efe 100%)',
                                                opacity: 1,
                                              },
                                          }}
                                        />
                                      </Tooltip>
                                    </Box>
                                    <Button
                                      size="small"
                                      variant={
                                        isCurrent ? 'outlined' : 'contained'
                                      }
                                      disabled={
                                        busy || impersonateBusy || isCurrent
                                      }
                                      onClick={() =>
                                        handleImpersonateBusiness(
                                          biz.BusinessId,
                                        )
                                      }
                                      sx={{
                                        textTransform: 'none',
                                        minWidth: 170,
                                        borderRadius: '10px',
                                        ...(isCurrent
                                          ? {
                                              color: '#0d4efe',
                                              borderColor:
                                                'rgba(13, 78, 254, 0.4)',
                                            }
                                          : {
                                              color: '#fff',
                                              background:
                                                'linear-gradient(90deg, #01ebb2 0%, #0d4efe 100%)',
                                            }),
                                      }}
                                    >
                                      {impersonateBusy
                                        ? 'Applying...'
                                        : isCurrent
                                          ? 'Currently selected'
                                          : 'Impersonate'}
                                    </Button>
                                  </Box>
                                </ListItem>
                              </React.Fragment>
                            );
                          })}
                        </List>
                        )}
                      </>
                    )}
                    {tabValue === 'reviews' && (
                      <Box>
                        {reviewsLoading && (
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              py: 6,
                              px: 2,
                            }}
                          >
                            <NkySpinner />
                          </Box>
                        )}
                        {!reviewsLoading && reviewsError && (
                          <Alert
                            severity="error"
                            sx={{ borderRadius: 2, m: 1 }}
                          >
                            {reviewsError}
                          </Alert>
                        )}
                        {!reviewsLoading &&
                          !reviewsError &&
                          reviewGroups.length === 0 && (
                            <Typography sx={{ px: 2, py: 3, color: '#5a6478' }}>
                              No pending reviews to triage.
                            </Typography>
                          )}
                        {!reviewsLoading &&
                          !reviewsError &&
                          reviewGroups.map((group) => (
                            <Accordion
                              key={group.businessId}
                              disableGutters
                              sx={{
                                border: '1px solid rgba(13, 78, 254, 0.12)',
                                borderRadius: '12px !important',
                                mb: 1.5,
                                overflow: 'hidden',
                                '&:before': { display: 'none' },
                              }}
                            >
                              <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{
                                  background:
                                    'linear-gradient(135deg, rgba(1, 235, 178, 0.12) 0%, rgba(13, 78, 254, 0.08) 100%)',
                                  borderBottom:
                                    '1px solid rgba(13, 78, 254, 0.08)',
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    width: '100%',
                                    pr: 1,
                                    gap: 1,
                                  }}
                                >
                                  <Typography
                                    sx={{ fontWeight: 700, color: '#1a1a2e' }}
                                  >
                                    {group.businessName}
                                  </Typography>
                                  <Chip
                                    label={`${group.reviews.length} pending`}
                                    size="small"
                                    sx={{
                                      bgcolor: 'rgba(13, 78, 254, 0.12)',
                                      color: '#0d4efe',
                                      fontWeight: 600,
                                    }}
                                  />
                                </Box>
                              </AccordionSummary>
                              <AccordionDetails sx={{ py: 0.5 }}>
                                <List disablePadding>
                                  {group.reviews.map((review, index) => {
                                    const reviewKey = `${group.businessId}:${review.ReviewId}`;
                                    const busy =
                                      pendingReviewIds.has(reviewKey);
                                    const ratingValue = Math.max(
                                      0,
                                      Math.min(5, Number(review.Rating) || 0),
                                    );
                                    return (
                                      <React.Fragment key={review.ReviewId}>
                                        {index > 0 && (
                                          <Divider
                                            component="li"
                                            sx={{ opacity: 0.6 }}
                                          />
                                        )}
                                        <ListItem
                                          sx={{
                                            py: 1.5,
                                            alignItems: 'flex-start',
                                            flexWrap: 'wrap',
                                            gap: 1.5,
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              flex: '1 1 320px',
                                              minWidth: 0,
                                            }}
                                          >
                                            <Box
                                              sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                flexWrap: 'wrap',
                                                mb: 0.5,
                                              }}
                                            >
                                              <Typography
                                                sx={{
                                                  fontWeight: 600,
                                                  color: '#1a1a2e',
                                                }}
                                              >
                                                {`${review.FirstName || ''} ${review.LastName || ''}`.trim() ||
                                                  'Anonymous'}
                                              </Typography>
                                              <Rating
                                                value={ratingValue}
                                                readOnly
                                                size="small"
                                                sx={{ verticalAlign: 'middle' }}
                                              />
                                            </Box>
                                            {review.ReviewText ? (
                                              <Typography
                                                variant="body2"
                                                sx={{ color: '#5a6478' }}
                                              >
                                                {review.ReviewText}
                                              </Typography>
                                            ) : (
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  color: '#8a93a5',
                                                  fontStyle: 'italic',
                                                }}
                                              >
                                                No written comment.
                                              </Typography>
                                            )}
                                          </Box>
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              flexDirection: 'column',
                                              alignItems: 'flex-end',
                                              gap: 1,
                                              minWidth: 200,
                                            }}
                                          >
                                            <Box
                                              sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                              }}
                                            >
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  color: '#5a6478',
                                                  minWidth: 118,
                                                }}
                                              >
                                                {busy
                                                  ? 'Saving…'
                                                  : 'Done reviewing'}
                                              </Typography>
                                              <Switch
                                                checked={false}
                                                disabled={busy}
                                                onChange={(_, checked) => {
                                                  if (checked) {
                                                    handleMarkReviewed(
                                                      group.businessId,
                                                      review.ReviewId,
                                                    );
                                                  }
                                                }}
                                                inputProps={{
                                                  'aria-label': `Mark review as done for ${group.businessName}`,
                                                }}
                                                sx={{
                                                  '& .MuiSwitch-switchBase.Mui-checked':
                                                    {
                                                      color: '#01ebb2',
                                                    },
                                                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':
                                                    {
                                                      background:
                                                        'linear-gradient(90deg, #01ebb2 0%, #0d4efe 100%)',
                                                      opacity: 1,
                                                    },
                                                }}
                                              />
                                            </Box>
                                            <Box
                                              sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                              }}
                                            >
                                              <Typography
                                                variant="body2"
                                                sx={{
                                                  color: '#5a6478',
                                                  minWidth: 118,
                                                }}
                                              >
                                                Published
                                              </Typography>
                                              <Switch
                                                checked={Boolean(
                                                  review.IsPublished,
                                                )}
                                                disabled={busy}
                                                onChange={(_, checked) =>
                                                  handleReviewPublishedToggle(
                                                    group.businessId,
                                                    review.ReviewId,
                                                    checked,
                                                  )
                                                }
                                                inputProps={{
                                                  'aria-label': `${review.IsPublished ? 'Unpublish' : 'Publish'} review for ${group.businessName}`,
                                                }}
                                                sx={{
                                                  '& .MuiSwitch-switchBase.Mui-checked':
                                                    {
                                                      color: '#01ebb2',
                                                    },
                                                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':
                                                    {
                                                      background:
                                                        'linear-gradient(90deg, #01ebb2 0%, #0d4efe 100%)',
                                                      opacity: 1,
                                                    },
                                                }}
                                              />
                                            </Box>
                                          </Box>
                                        </ListItem>
                                      </React.Fragment>
                                    );
                                  })}
                                </List>
                              </AccordionDetails>
                            </Accordion>
                          ))}
                      </Box>
                    )}
                  </>
                )}
            </AdminListSection>
          </ContentWrapper>
        </DashboardContainer>
      </AdminPageOuter>
      <AdminBusinessEditModal
        open={Boolean(editBusiness)}
        business={editBusiness}
        formValues={editForm}
        onFieldChange={handleEditFieldChange}
        onSave={handleBusinessEditSave}
        onClose={closeBusinessEdit}
        saving={editSaving}
        saveDisabled={
          !editBusiness || isAdminBusinessEditUnchanged(editForm, editBusiness)
        }
      />
      <NkySnackbarAlert
        open={toast.open}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        severity={toast.severity}
        message={toast.message}
      />
    </AppProvider>
  );
}

export default AdminPage;
